import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FlaskConical, Loader2, Sparkles, ArrowRight, Download, PlayCircle, Users, ClipboardList,
  DollarSign, CheckCircle2, AlertTriangle, MinusCircle, Clock, Rocket, HelpCircle,
  MessageSquare, FileText, Layout as LayoutIcon, Search, UserSquare2, TrendingUp,
} from "lucide-react";
import { AIResourcesShell, StepLabel } from "../../components/ai/AIResourcesShell";
import { ResultPaywall } from "../../components/ai/ResultPaywall";
import {
  FaTiktok, FaInstagram, FaYoutube, FaReddit, FaLinkedin, FaFacebookF, FaTwitter,
  FaDiscord, FaPinterest, FaSlack, FaWhatsapp, FaTelegram, FaProductHunt, FaGithub,
  FaTwitch, FaMedium, FaKickstarterK, FaEnvelope, FaUsers, FaGlobe,
} from "react-icons/fa";
import { aiValidate, aiRoadmap, aiSave, aiListSaved } from "../../lib/api";
import { useAIResources } from "../../hooks/useAIResources";
import { useAuth } from "../../hooks/useAuth";

const STEPS = [
  { n: 1, title: "Idea Details", sub: "Tell us about your idea" },
  { n: 2, title: "Market Analysis", sub: "AI analyzes the market" },
  { n: 3, title: "Validation Plan", sub: "Get your action plan" },
  { n: 4, title: "Results & Report", sub: "See your validation score" },
];

const BREAKDOWN_LABELS = {
  market_demand: "Market Demand", customer_pain: "Customer Pain", solution_uniqueness: "Solution Uniqueness",
  competition: "Competition", ease_of_validation: "Ease of Validation", monetization_potential: "Monetization Potential",
};

const PLAN = [
  { icon: Users, title: "Customer Interviews", steps: ["Interview 10 people", "Ask key questions", "Identify core problems"], time: "3–5 Days" },
  { icon: ClipboardList, title: "Survey", steps: ["Build survey", "Collect 50 responses", "Analyze feedback"], time: "5–7 Days" },
  { icon: LayoutIcon, title: "Landing Page Test", steps: ["Create landing page", "Add waitlist", "Measure sign-ups"], time: "3–5 Days" },
  { icon: DollarSign, title: "Demand Check", steps: ["Test pricing", "Validate willingness to pay", "Measure interest"], time: "5–7 Days" },
];

const TOOLKIT = ["Interview Questions", "Survey Builder", "Landing Page Checklist", "Competitor Research", "Customer Persona", "Market Size Estimator"];
const TOOLKIT_ICONS = [MessageSquare, FileText, LayoutIcon, Search, UserSquare2, TrendingUp];

const toneIcon = (t) => (t === "positive" ? CheckCircle2 : t === "caution" ? AlertTriangle : MinusCircle);
const toneColor = (t) => (t === "positive" ? "text-green-500" : t === "caution" ? "text-amber-500" : "text-[#687280]");
const scoreColor = (s) => (s >= 80 ? "#22c55e" : s >= 60 ? "#D4AF37" : "#ef4444");
const priorityPill = (p) => (p === "High" ? "bg-[#D4AF37]/15 text-[#8a6d1a]" : p === "Medium" ? "bg-amber-50 text-amber-600" : "bg-gray-100 text-[#687280]");

const CHANNEL_MAP = [
  ["tiktok", FaTiktok, "#000000"], ["instagram", FaInstagram, "#E4405F"], ["youtube", FaYoutube, "#FF0000"],
  ["reddit", FaReddit, "#FF4500"], ["linkedin", FaLinkedin, "#0A66C2"], ["facebook", FaFacebookF, "#1877F2"],
  ["kickstarter", FaKickstarterK, "#05CE78"], ["twitter", FaTwitter, "#1DA1F2"], ["discord", FaDiscord, "#5865F2"],
  ["pinterest", FaPinterest, "#BD081C"], ["slack", FaSlack, "#4A154B"], ["whatsapp", FaWhatsapp, "#25D366"],
  ["telegram", FaTelegram, "#26A5E4"], ["product hunt", FaProductHunt, "#DA552F"], ["producthunt", FaProductHunt, "#DA552F"],
  ["github", FaGithub, "#181717"], ["twitch", FaTwitch, "#9146FF"], ["medium", FaMedium, "#000000"],
  ["email", FaEnvelope, "#B8901F"], ["newsletter", FaEnvelope, "#B8901F"], ["outreach", FaEnvelope, "#B8901F"],
  ["influencer", FaUsers, "#8a6d1a"], ["creator", FaUsers, "#8a6d1a"], ["communit", FaUsers, "#8a6d1a"],
  ["group", FaUsers, "#1877F2"], ["forum", FaUsers, "#8a6d1a"], ["trade show", FaUsers, "#8a6d1a"], ["event", FaUsers, "#8a6d1a"],
];
const channelIcon = (name = "") => {
  const n = name.toLowerCase();
  const hit = CHANNEL_MAP.find(([k]) => n.includes(k));
  return hit ? { Icon: hit[1], color: hit[2] } : { Icon: FaGlobe, color: "#D4AF37" };
};
const channelUrl = (platform = "", query = "") => {
  const p = platform.toLowerCase();
  const q = encodeURIComponent((query || platform).trim());
  const g = (t) => `https://www.google.com/search?q=${encodeURIComponent(t)}`;
  if (p.includes("tiktok")) return `https://www.tiktok.com/search?q=${q}`;
  if (p.includes("instagram")) return `https://www.instagram.com/explore/search/keyword/?q=${q}`;
  if (p.includes("youtube")) return `https://www.youtube.com/results?search_query=${q}`;
  if (p.includes("reddit")) { const m = platform.match(/r\/(\w+)/i); return m ? `https://www.reddit.com/r/${m[1]}/` : `https://www.reddit.com/search/?q=${q}`; }
  if (p.includes("kickstarter")) return `https://www.kickstarter.com/discover/advanced?term=${q}`;
  if (p.includes("linkedin")) return `https://www.linkedin.com/search/results/all/?keywords=${q}`;
  if (p.includes("facebook")) return `https://www.facebook.com/search/groups/?q=${q}`;
  if (p.includes("twitter") || p === "x") return `https://twitter.com/search?q=${q}`;
  if (p.includes("discord")) return `https://disboard.org/search?keyword=${q}`;
  if (p.includes("pinterest")) return `https://www.pinterest.com/search/pins/?q=${q}`;
  if (p.includes("product hunt") || p.includes("producthunt")) return `https://www.producthunt.com/search?q=${q}`;
  if (p.includes("github")) return `https://github.com/search?q=${q}`;
  if (p.includes("twitch")) return `https://www.twitch.tv/search?term=${q}`;
  if (p.includes("medium")) return `https://medium.com/search?q=${q}`;
  if (p.includes("stockx")) return `https://stockx.com/search?s=${q}`;
  if (p.includes("goat")) return `https://www.goat.com/search?query=${q}`;
  if (p.includes("email") || p.includes("outreach") || p.includes("newsletter")) return g(`cold email outreach ${query}`);
  return g(`${platform} ${query}`);
};

export default function ValidationLab() {
  const { validation, patchValidation } = useAIResources();
  const { user, requireAuth } = useAuth();
  const navigate = useNavigate();
  const { form, report, history, roadmap, roadmapDone } = validation;
  const setForm = (u) => patchValidation({ form: typeof u === "function" ? u(form) : u });
  const setReport = (v) => patchValidation({ report: v });
  const setHistory = (u) => patchValidation({ history: typeof u === "function" ? u(history) : u });
  const setRoadmap = (v) => patchValidation({ roadmap: v });
  const toggleStep = (i) => patchValidation({ roadmapDone: (roadmapDone || []).includes(i) ? roadmapDone.filter((x) => x !== i) : [...(roadmapDone || []), i] });
  const [loading, setLoading] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [activeInsight, setActiveInsight] = useState(null);
  const [showHow, setShowHow] = useState(false);
  const [planSaved, setPlanSaved] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const savePlan = () => {
    if (planSaved) { toast.info("Already saved — open your Startup Notebook to check it off"); return; }
    const steps = PLAN.map((p) => ({ title: p.title, description: `${p.steps.join(" • ")} — ${p.time}` }));
    const doSave = async () => {
      try {
        const existing = await aiListSaved("plan").catch(() => []);
        const pname = form.name || report?.name || "My Startup";
        if ((existing || []).some((p) => (p.data?.name || "").toLowerCase() === pname.toLowerCase())) {
          setPlanSaved(true);
          toast.info("Already in your Startup Notebook");
          return;
        }
        await aiSave("plan", { name: pname, steps, done: [] });
        setPlanSaved(true);
        toast.success("Validation plan saved to your Startup Notebook");
      } catch { toast.error("Could not save the plan"); }
    };
    if (user) doSave(); else requireAuth(doSave);
  };
  const currentStep = loading ? 2 : report ? 4 : 1;

  const analyze = async () => {
    if (!form.name.trim() || !form.does.trim()) { toast.error("Add your startup name and what it does"); return; }
    setLoading(true); setReport(null);
    try {
      const r = await aiValidate(form);
      setReport(r);
      setHistory((h) => [{ name: r.name, score: r.score, id: Date.now() }, ...h].slice(0, 6));
      setTimeout(() => document.getElementById("val-score")?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
    } catch { toast.error("Could not analyze. Please try again."); }
    finally { setLoading(false); }
  };

  const reset = () => { setReport(null); setForm({ name: "", does: "", customer: "", problem: "", different: "" }); document.getElementById("val-form")?.scrollIntoView({ behavior: "smooth" }); };
  const soon = (label) => toast.info(`${label} — coming soon`);

  const actions = null;

  const sidebar = (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-5">
        <Rocket size={20} className="text-[#D4AF37]" />
        <p className="font-display text-lg font-bold mt-2 leading-tight">Validate Smarter.<br />Build With Confidence.</p>
        <p className="text-sm text-[#c9c39f] mt-2 leading-snug">Test your idea, know your market, and launch with real data behind you.</p>
        <button onClick={reset} className="mt-4 w-full bg-[#D4AF37] text-[#0F0F0F] rounded-full py-2.5 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="sidebar-new-validation">Start New Validation</button>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <div className="flex items-center justify-between">
          <p className="font-display font-bold text-[#0F0F0F]">My Validations</p>
          <span className="text-xs bg-[#F7F7F7] rounded-full px-2 py-0.5 text-[#687280]" data-testid="val-history-count">{history.length}</span>
        </div>
        <div className="mt-3 space-y-2.5">
          {history.length === 0 ? (
            <p className="text-xs text-[#9aa1ab]">Run a validation to see it here.</p>
          ) : history.map((h) => (
            <div key={h.id} className="flex items-center justify-between gap-2" data-testid={`val-history-${h.id}`}>
              <div className="flex items-center gap-2 min-w-0"><FlaskConical size={14} className="text-[#D4AF37] shrink-0" /><span className="text-sm font-semibold text-[#0F0F0F] truncate">{h.name}</span></div>
              <span className="text-xs font-bold" style={{ color: scoreColor(h.score) }}>{h.score}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <p className="font-display font-bold text-[#0F0F0F]">Validation Toolkit</p>
        <div className="mt-3 space-y-1">
          {TOOLKIT.map((t, i) => {
            const Icon = TOOLKIT_ICONS[i];
            return (
              <button key={t} onClick={() => navigate("/ai-resources/tool-recommendations")} className="w-full flex items-center gap-2.5 text-sm text-[#374151] hover:text-[#0F0F0F] py-1.5" data-testid={`val-toolkit-${i}`}>
                <Icon size={15} className="text-[#9aa1ab]" /> {t}
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <div className="flex items-center gap-2"><HelpCircle size={16} className="text-[#D4AF37]" /><p className="font-semibold text-[#0F0F0F] text-sm">Need Help?</p></div>
        <p className="text-xs text-[#687280] mt-1.5">Learn how to validate your startup idea step-by-step.</p>
        <button onClick={() => navigate("/learn")} className="mt-3 w-full border border-gray-200 rounded-full py-2 text-xs font-semibold text-[#374151] hover:border-[#D4AF37] transition-colors" data-testid="validation-view-guide">View Guide</button>
      </div>
    </>
  );

  return (
    <AIResourcesShell
      active="validation-lab"
      title={<span className="inline-flex items-center gap-2">Validation Lab <span className="text-[10px] font-bold bg-[#D4AF37]/15 text-[#8a6d1a] px-2 py-0.5 rounded-full align-middle">BETA</span></span>}
      subtitle="Test your startup idea with AI and receive a complete validation report before you build."
      icon={FlaskConical}
      actions={actions}
      extra={sidebar}
    >
      {/* Progress steps */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="val-progress">
        {STEPS.map((s, i) => {
          const active = s.n === currentStep;
          const done = s.n < currentStep;
          return (
            <div key={s.n} className={`rounded-2xl border p-4 flex items-center gap-3 ${active ? "border-[#D4AF37] bg-[#FBF7EA]" : done ? "border-green-200 bg-green-50/50" : "border-gray-100 bg-white"}`}>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${active ? "bg-[#D4AF37] text-[#0F0F0F]" : done ? "bg-green-500 text-white" : "bg-gray-100 text-[#9aa1ab]"}`}>
                {done ? <CheckCircle2 size={16} /> : s.n}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-[#0F0F0F] truncate">{s.title}</p>
                <p className="text-[11px] text-[#687280] truncate">{s.sub}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Section 1 + 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" id="val-form">
        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="val-idea-form">
          <StepLabel n={1} />
          <h2 className="font-display text-xl font-bold text-[#0F0F0F] mt-1">Describe Your Idea</h2>
          <p className="text-sm text-[#687280] mt-1">The more details you provide, the better our analysis.</p>
          <div className="space-y-4 mt-5">
            <Field label="Startup Name"><input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. SmartMenu AI" className="val-inp" data-testid="val-name" /></Field>
            <Field label="What does your startup do?"><textarea value={form.does} onChange={(e) => set("does", e.target.value)} rows={2} maxLength={300} placeholder="An AI-powered menu optimizer for restaurants..." className="val-inp resize-none" data-testid="val-does" /></Field>
            <Field label="Who is your target customer?"><input value={form.customer} onChange={(e) => set("customer", e.target.value)} placeholder="e.g. Restaurant Owners" className="val-inp" data-testid="val-customer" /></Field>
            <Field label="What problem does it solve?"><textarea value={form.problem} onChange={(e) => set("problem", e.target.value)} rows={2} maxLength={300} placeholder="Restaurant owners struggle with menu pricing..." className="val-inp resize-none" data-testid="val-problem" /></Field>
            <Field label="How is it different?"><textarea value={form.different} onChange={(e) => set("different", e.target.value)} rows={2} maxLength={300} placeholder="We use AI to analyze sales data and trends..." className="val-inp resize-none" data-testid="val-different" /></Field>
          </div>
          <button onClick={analyze} disabled={loading} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="val-analyze-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Analyze My Idea
          </button>
        </div>

        {/* Score */}
        <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]" id="val-score" data-testid="val-score">
          <div className="flex items-center gap-2"><h2 className="font-display text-xl font-bold text-[#0F0F0F]">Validation Score</h2><Sparkles size={15} className="text-[#D4AF37]" /></div>
          {!report ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              {loading ? <Loader2 size={32} className="animate-spin text-[#D4AF37]" /> : <FlaskConical size={40} className="text-gray-200" />}
              <p className="text-sm text-[#9aa1ab] mt-4 max-w-xs">{loading ? "Analyzing your idea with AI…" : "Fill in your idea and click Analyze to generate your validation score."}</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row items-center gap-6 mt-4">
                <ScoreRing score={report.score} verdict={report.verdict} />
                <div className="flex-1">
                  <p className="text-sm text-[#374151] leading-relaxed">{report.summary}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-sm text-[#687280]">Confidence Level</span>
                    <span className="text-xs font-bold bg-[#D4AF37]/15 text-[#8a6d1a] px-2.5 py-1 rounded-full">{report.confidence}</span>
                  </div>
                </div>
              </div>
              <p className="font-display font-bold text-[#0F0F0F] mt-6 mb-2">Score Breakdown</p>
              <div className="space-y-3">
                {Object.entries(BREAKDOWN_LABELS).map(([k, label]) => (
                  <Bar key={k} label={label} value={report.breakdown[k]} testid={`val-bar-${k}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {report && (
        <ResultPaywall label="validation report" testid="val-paywall"><>
          {/* Validation Plan */}
          <section className="mt-8" data-testid="val-plan">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-[#0F0F0F]">Your Validation Plan</h2>
              <button onClick={() => setShowCustomize(true)} className="inline-flex items-center gap-1.5 bg-[#0F0F0F] text-[#D4AF37] rounded-full px-4 py-2 text-sm font-bold hover:bg-[#1c1c1c] transition-colors shadow-sm" data-testid="val-customize-btn"><Sparkles size={15} /> Customize Plan</button>
            </div>
            <p className="text-sm text-[#687280] mt-1">Follow this step-by-step plan to validate your idea.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-5">
              {PLAN.map((p, i) => (
                <div key={p.title} className="rounded-2xl border border-gray-100 bg-white p-5 flex flex-col" data-testid={`val-plan-${i}`}>
                  <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/12 text-[#B8901F] flex items-center justify-center"><p.icon size={18} /></span>
                  <h3 className="font-display font-bold text-[#0F0F0F] mt-3">{i + 1}. {p.title}</h3>
                  <ul className="mt-2 space-y-1 text-sm text-[#687280] flex-1">
                    {p.steps.map((s) => <li key={s} className="flex gap-1.5"><CheckCircle2 size={14} className="mt-0.5 shrink-0 text-[#D4AF37]" />{s}</li>)}
                  </ul>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#8a6d1a]"><Clock size={13} /> {p.time}</span>
                    <button onClick={savePlan} className="text-xs font-bold bg-[#0F0F0F] text-white rounded-full px-4 py-1.5 hover:bg-[#1c1c1c] transition-colors" data-testid={`val-plan-start-${i}`}>Start</button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Personalized Roadmap */}
          {roadmap && (
            <section className="mt-8" data-testid="val-roadmap">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-display text-xl font-bold text-[#0F0F0F]">Your Personalized Roadmap</h2>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-[#D4AF37]/15 text-[#8a6d1a] px-2.5 py-1 rounded-full"><Sparkles size={12} /> Tailored for {roadmap.business_type}</span>
              </div>
              <p className="text-sm text-[#687280] mt-1">Same proven roadmap — guidance personalized to your startup and stage.</p>

              {/* Progress */}
              <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-4" data-testid="val-roadmap-progress">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-semibold text-[#0F0F0F]">Your Progress</span>
                  <span className="font-bold text-[#8a6d1a]" data-testid="val-roadmap-progress-count">{(roadmapDone || []).length} of {roadmap.steps.length} complete</span>
                </div>
                <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full bg-[#D4AF37] transition-all duration-500" style={{ width: `${Math.round(((roadmapDone || []).length / roadmap.steps.length) * 100)}%` }} />
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {roadmap.steps.map((s, i) => {
                  const done = (roadmapDone || []).includes(i);
                  return (
                    <div key={s.title} className={`rounded-2xl border p-4 flex items-start gap-4 transition-colors ${done ? "border-green-200 bg-green-50/50" : "border-gray-100 bg-white"}`} data-testid={`val-roadmap-step-${i}`}>
                      <button onClick={() => toggleStep(i)} data-testid={`val-roadmap-toggle-${i}`} aria-pressed={done} className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${done ? "bg-green-500 text-white" : "bg-[#0F0F0F] text-[#D4AF37] hover:bg-[#1c1c1c]"}`}>
                        {done ? <CheckCircle2 size={18} /> : i + 1}
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className={`font-display font-bold ${done ? "text-[#687280] line-through" : "text-[#0F0F0F]"}`}>{s.title}</p>
                        <p className="text-sm text-[#687280] mt-0.5 leading-snug">{s.description}</p>
                      </div>
                      <button onClick={() => toggleStep(i)} className={`text-xs font-bold rounded-full px-3 py-1.5 shrink-0 transition-colors ${done ? "bg-green-100 text-green-600" : "border border-gray-200 text-[#374151] hover:border-[#D4AF37]"}`} data-testid={`val-roadmap-mark-${i}`}>
                        {done ? "Done" : "Mark done"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Key Insights */}
          <section className="mt-8" data-testid="val-insights">
            <h2 className="font-display text-xl font-bold text-[#0F0F0F]">Key Insights <span className="text-sm font-semibold text-[#9aa1ab]">(AI Analysis)</span></h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-5">
              {report.insights.map((ins, i) => {
                const Icon = toneIcon(ins.tone);
                return (
                  <div key={i} className="rounded-2xl border border-gray-100 bg-white p-5" data-testid={`val-insight-${i}`}>
                    <div className="flex items-center gap-2"><Icon size={18} className={toneColor(ins.tone)} /><p className="font-bold text-[#0F0F0F] text-sm">{ins.title}</p></div>
                    <p className="text-sm text-[#687280] mt-2 leading-snug">{ins.summary}</p>
                    <button onClick={() => setActiveInsight(ins)} className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#8a6d1a] hover:text-[#0F0F0F]" data-testid={`val-insight-view-${i}`}>View Insight <ArrowRight size={13} /></button>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Customers + Channels + Next step */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
            <div className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="val-customers">
              <h3 className="font-display font-bold text-[#0F0F0F]">Who should buy this?</h3>
              <p className="text-xs text-[#687280] mt-1">Who you should talk to first.</p>
              <div className="mt-4 space-y-3">
                {report.target_customers.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0"><Users size={16} className="text-[#D4AF37] shrink-0" /><div className="min-w-0"><p className="text-sm font-semibold text-[#0F0F0F] truncate">{c.name}</p>{c.note && <p className="text-[11px] text-[#9aa1ab] truncate">{c.note}</p>}</div></div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${priorityPill(c.priority)}`}>{c.priority}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="val-channels">
              <h3 className="font-display font-bold text-[#0F0F0F]">Where to Find Them</h3>
              <p className="text-xs text-[#687280] mt-1">Best places to reach your target customers.</p>
              <div className="mt-4 space-y-3">
                {report.channels.map((ch, i) => {
                  const { Icon, color } = channelIcon(ch.platform);
                  return (
                    <div key={i} className="flex items-start justify-between gap-3" data-testid={`val-channel-${i}`}>
                      <div className="flex items-start gap-3 min-w-0">
                        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1A`, color }}><Icon size={17} /></span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#0F0F0F]">{ch.platform}</p>
                          <p className="text-[12px] text-[#687280] leading-snug">{ch.description}</p>
                        </div>
                      </div>
                      <button onClick={() => window.open(channelUrl(ch.platform, form.customer || form.name), "_blank", "noopener")} className="text-xs font-semibold text-[#687280] hover:text-[#0F0F0F] shrink-0 mt-1" data-testid={`val-channel-view-${i}`}>View</button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border-2 border-[#D4AF37] bg-[#FBF7EA] p-6" data-testid="val-next-step">
              <div className="flex items-center gap-2"><Sparkles size={16} className="text-[#D4AF37]" /><p className="text-[11px] font-bold uppercase tracking-wide text-[#8a6d1a]">Recommended Next Step</p></div>
              <h3 className="font-display text-lg font-extrabold text-[#0F0F0F] mt-1">{report.next_step?.action}</h3>
              <p className="text-sm text-[#687280] mt-1">{report.next_step?.note}</p>
              <ul className="mt-4 space-y-2">
                {(report.next_step?.checklist || []).map((c, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#374151]"><CheckCircle2 size={15} className="text-[#D4AF37] shrink-0" />{c}</li>
                ))}
              </ul>
              <button onClick={() => soon("Start Interviews")} className="mt-4 w-full bg-[#D4AF37] text-[#0F0F0F] rounded-full py-2.5 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="val-start-interviews">Start {report.next_step?.action || "Now"}</button>
              <button onClick={() => soon("Interview Questions")} className="mt-2 w-full border border-gray-200 rounded-full py-2 text-xs font-semibold text-[#374151] hover:border-[#D4AF37] transition-colors">Interview Questions</button>
            </div>
          </div>
        </></ResultPaywall>
      )}

      {showHow && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowHow(false)} data-testid="how-it-works-modal">
          <div className="bg-white rounded-3xl w-full max-w-md p-7 shadow-2xl text-center" onClick={(e) => e.stopPropagation()}>
            <span className="w-14 h-14 rounded-2xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center mx-auto"><FlaskConical size={26} /></span>
            <h2 className="font-display text-2xl font-extrabold text-[#0F0F0F] mt-4">How Validation Lab Works</h2>
            <p className="text-base text-[#374151] leading-relaxed mt-3">Great startups begin with validated ideas. Use proven validation methods to understand your customers, test demand, and build with confidence instead of guesswork.</p>
            <button onClick={() => setShowHow(false)} className="mt-6 w-full bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="how-it-works-done">Got it</button>
          </div>
        </div>
      )}

      {activeInsight && (
        <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActiveInsight(null)} data-testid="insight-modal">
          <div className="bg-white rounded-3xl w-full max-w-md p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                {(() => { const Icon = toneIcon(activeInsight.tone); return <Icon size={26} className={toneColor(activeInsight.tone)} />; })()}
                <h2 className="font-display text-xl font-extrabold text-[#0F0F0F]">{activeInsight.title}</h2>
              </div>
              <button onClick={() => setActiveInsight(null)} className="text-[#687280] hover:text-[#0F0F0F] shrink-0 ml-2" data-testid="insight-modal-close">✕</button>
            </div>
            <p className="text-base text-[#374151] leading-relaxed mt-4">{activeInsight.summary}</p>
            <button onClick={() => setActiveInsight(null)} className="mt-6 w-full bg-[#0F0F0F] text-white rounded-full py-3 text-sm font-bold hover:bg-[#1c1c1c] transition-colors" data-testid="insight-modal-done">Got it</button>
          </div>
        </div>
      )}

      {showCustomize && (
        <CustomizeRoadmapModal
          defaultBusinessType={form.customer || form.name || ""}
          name={form.name}
          does={form.does}
          onClose={() => setShowCustomize(false)}
          onGenerated={(r) => { patchValidation({ roadmap: r, roadmapDone: [] }); setShowCustomize(false); if (user) { aiSave("roadmap", { business_type: r.business_type, name: form.name, steps: r.steps, done: [] }).catch(() => {}); } setTimeout(() => document.getElementById("val-roadmap")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200); }}
        />
      )}

      {/* Bottom banner */}
      <div className="mt-8 rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-6 md:p-8 flex flex-wrap items-center gap-4">
        <div>
          <p className="font-display text-xl font-extrabold text-[#D4AF37]">Validate Today. Launch With Confidence.</p>
          <p className="text-sm text-[#c9c39f] mt-1">Ideas are easy. Validation builds successful companies.</p>
        </div>
        <button onClick={reset} className="ml-auto inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="val-banner-new">Start New Validation <ArrowRight size={15} /></button>
      </div>
    </AIResourcesShell>
  );
}

const STAGES = [
  { emoji: "💡", label: "Just an idea" },
  { emoji: "🔍", label: "Validating my idea" },
  { emoji: "🛠", label: "Building my MVP" },
  { emoji: "🚀", label: "Ready to launch" },
];
const GOALS = ["Find customers", "Build faster", "Validate my idea", "Learn startup basics", "Launch my business"];

const CustomizeRoadmapModal = ({ defaultBusinessType, name, does, onClose, onGenerated }) => {
  const [stage, setStage] = useState("");
  const [businessType, setBusinessType] = useState(defaultBusinessType || "");
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!businessType.trim()) { toast.error("Add your business type"); return; }
    setBusy(true);
    try {
      const r = await aiRoadmap({ stage, business_type: businessType.trim(), goal, name, does });
      toast.success("Your roadmap is personalized");
      onGenerated(r);
    } catch { toast.error("Could not generate roadmap. Try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="customize-roadmap-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {busy ? (
          <div className="flex flex-col items-center justify-center text-center py-14" data-testid="customize-generating">
            <div className="relative w-16 h-16">
              <span className="absolute inset-0 rounded-full border-4 border-[#D4AF37]/20" />
              <Loader2 size={64} className="animate-spin text-[#D4AF37]" strokeWidth={2} />
            </div>
            <p className="font-display text-xl font-extrabold text-[#0F0F0F] mt-5 flex items-center gap-2"><Sparkles size={18} className="text-[#D4AF37]" /> Generating a plan…</p>
            <p className="text-sm text-[#687280] mt-2 max-w-xs">Blueprint AI is tailoring your 7-step roadmap to your startup, stage and goal.</p>
          </div>
        ) : (
        <>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-[#0F0F0F] flex items-center gap-2"><Sparkles size={20} className="text-[#D4AF37]" /> Customize Your Roadmap</h2>
            <p className="text-sm text-[#687280] mt-1.5">Tell Blueprint AI a little about your startup, and we'll personalize your roadmap.</p>
          </div>
          <button onClick={onClose} className="text-[#687280] hover:text-[#0F0F0F] shrink-0 ml-2" data-testid="customize-close">✕</button>
        </div>

        <div className="mt-6">
          <label className="text-xs font-bold uppercase tracking-wide text-[#374151]">Startup Stage</label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {STAGES.map((s) => (
              <button key={s.label} onClick={() => setStage(s.label)} className={`text-left text-sm rounded-xl border px-3.5 py-2.5 transition-colors ${stage === s.label ? "border-[#D4AF37] bg-[#FBF7EA] text-[#8a6d1a] font-semibold" : "border-gray-200 text-[#374151] hover:border-[#D4AF37]"}`} data-testid={`customize-stage-${s.label.toLowerCase().replace(/ /g, "-")}`}>
                <span className="mr-1.5">{s.emoji}</span>{s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <label className="text-xs font-bold uppercase tracking-wide text-[#374151]">Business Type</label>
          <input value={businessType} onChange={(e) => setBusinessType(e.target.value)} placeholder="Example: Restaurant SaaS, Fitness App, Marketplace, AI Tool" className="w-full mt-2 rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]" data-testid="customize-business-type" />
        </div>

        <div className="mt-5">
          <label className="text-xs font-bold uppercase tracking-wide text-[#374151]">Biggest Goal Right Now</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {GOALS.map((g) => (
              <button key={g} onClick={() => setGoal(g)} className={`text-sm rounded-full border px-3.5 py-2 transition-colors ${goal === g ? "border-[#D4AF37] bg-[#FBF7EA] text-[#8a6d1a] font-semibold" : "border-gray-200 text-[#374151] hover:border-[#D4AF37]"}`} data-testid={`customize-goal-${g.toLowerCase().replace(/ /g, "-")}`}>{g}</button>
            ))}
          </div>
        </div>

        <button onClick={generate} disabled={busy} className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full py-3.5 text-sm font-bold hover:bg-[#c5a030] transition-colors disabled:opacity-60" data-testid="customize-generate-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Generate My Roadmap
        </button>
        <p className="text-[11px] text-[#9aa1ab] text-center mt-3">The roadmap steps stay the same — only the guidance is tailored to you.</p>
        </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold text-[#374151]">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const Bar = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center justify-between text-sm mb-1"><span className="text-[#374151]">{label}</span><span className="font-bold text-[#0F0F0F]">{value}%</span></div>
    <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${value}%`, background: scoreColor(value) }} /></div>
  </div>
);

const ScoreRing = ({ score, verdict }) => {
  const r = 52, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  return (
    <div className="relative w-32 h-32 shrink-0">
      <svg width="128" height="128" className="-rotate-90">
        <circle cx="64" cy="64" r={r} fill="none" stroke="#f1f1f1" strokeWidth="10" />
        <circle cx="64" cy="64" r={r} fill="none" stroke={scoreColor(score)} strokeWidth="10" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-3xl font-extrabold text-[#0F0F0F]">{score}%</span>
        <span className="text-[11px] font-semibold text-[#8a6d1a]">{verdict}</span>
      </div>
    </div>
  );
};
